import React, { useContext } from "react";
import { Layout } from "views/Main/Layouts";
import { Form, Formik } from "collections";
import { Trans, useTranslation } from "utilities/i18n";
import { ToggleSwitchInput, Loader, HelpLink } from "elements";
import "./NotificationCenter.scss";
import EmailList from "./EmailList";
import "./NotificationCenter.scss";
import {
  useAudienceForUserQuery,
  useDisableNotificationsForUserMutation,
  useEnableNotificationsForUserMutation,
  useEnableActionMutation,
  ActionConfiguration
} from "generated/graphql";
import { NotificationTypes } from "./NotificationType";
import {
  NotificationFormProps,
  NotificationsSchema
} from "./NotificationsSchema";
import { usePolyPlusEnabled } from "utilities/polyPlus";
import { PolyPlusInactiveModal } from "elements/PolyPlus/PolyPlus";
import { PageTitle } from "./PageTitle";
import { DOCS } from "Constants";
import { SelectedTenantContext } from "polyrouter/SelectedTenantProvider";

const UserNotificationCenter: React.FC<{
  userId: string;
}> = ({ userId }) => {
  const { t } = useTranslation();
  const { tenant } = useContext(SelectedTenantContext);
  const [
    { fetching: enableFetching },
    setEnabledValue
  ] = useEnableNotificationsForUserMutation();
  const [, setDisabledValue] = useDisableNotificationsForUserMutation();

  const [{ fetching: enableActionFetching }] = useEnableActionMutation();

  const { polyPlusEnabled, fetching: polyPlusFetching } = usePolyPlusEnabled(
    tenant?.id
  );

  const { Content } = Layout;

  const { data: userAudienceData, fetching } = useAudienceForUserQuery({
    variables: { user_id: userId, tid: tenant?.id || "" },
    pause: !polyPlusEnabled || !tenant
  });

  // Grab data from the query
  let actionConfigs: ActionConfiguration[] = [];
  let audienceId: string | undefined;

  if (!fetching && userAudienceData && userAudienceData.ncPoly.userAudience) {
    actionConfigs = userAudienceData.ncPoly.userAudience.actionConfigurations;
    audienceId = userAudienceData.ncPoly.userAudience.id;
  }

  // Need a handleChange function for enablement, use ncEnableUser/ncDisableUser mutations
  async function handleToggleNotifications(
    setFieldValue: (
      field: string,
      value: any,
      shouldValidate?: boolean | undefined
    ) => void,
    values: {
      enablement: boolean;
      notificationTypes: {
        id: string;
        enabled: boolean;
        actionType: { id: string; type: string };
      }[];
    }
  ) {
    const enabled = values.enablement;
    if (!enabled) {
      const enableResponse = await setEnabledValue({
        tid: tenant?.id || "",
        user_id: userId
      });
      if (!enableResponse.error && enableResponse.data) {
        setFieldValue("enablement", enableResponse.data?.ncEnableUser.enabled);
        const actionsSettings =
          enableResponse.data?.ncEnableUser.actionConfigurations;
        const recipientSettings = enableResponse.data?.ncEnableUser.recipients;

        if (actionsSettings && actionsSettings.length > 0) {
          actionConfigs = actionsSettings;
          setFieldValue("notificationTypes", actionsSettings);
        }
        if (recipientSettings && recipientSettings.length > 0) {
          setFieldValue("recipients", recipientSettings);
        }
      }
    } else if (enabled) {
      const disableResponse = await setDisabledValue({
        tid: tenant?.id || "",
        user_id: userId
      });
      if (!disableResponse.error && disableResponse.data) {
        setFieldValue("enablement", disableResponse.data.ncDisableUser.enabled);
      }
    }
  }

  let initialValues: NotificationFormProps = {
    newRecipient: NotificationsSchema.fields.newRecipient.default(),
    recipients: NotificationsSchema.fields.recipients.default(),
    enablement: NotificationsSchema.fields.enablement.default(),
    notificationTypes: NotificationsSchema.fields.notificationTypes.default()
  };

  if (userAudienceData && userAudienceData.ncPoly.userAudience) {
    const audience = userAudienceData.ncPoly.userAudience;

    const values: Pick<
      NotificationFormProps,
      "recipients" | "enablement" | "notificationTypes"
    > = {
      recipients: audience.recipients.length ? audience.recipients : [],
      enablement: audience.enabled,
      notificationTypes: audience.actionConfigurations
    };
    initialValues = { ...initialValues, ...values };
  }
  return (
    <Loader loading={fetching || polyPlusFetching}>
      <Formik
        validationSchema={NotificationsSchema}
        initialValues={initialValues}
        name="UserNotificationCenter"
        onSubmit={() => {}}
        validateOnChange
        enableReinitialize
      >
        {({ isValid, values, setFieldValue, errors, initialValues }) => (
          <Content scrolling>
            <Content.ScrollingList>
              <div
                className="notification-center-content"
                data-testid="user-notification-center"
              >
                <PageTitle titleKey="NOTIFICATION_POLY_TITLE" />
                <div className="options-section">
                  <Trans i18nKey={`NOTIFICATION_EMAILS_HELP`}>
                    <p>
                      Description: To learn more, click
                      <HelpLink location={DOCS.NOTIFICATION_OVERVIEW}>
                        here
                      </HelpLink>
                    </p>
                  </Trans>
                </div>
                <Form className="form grid">
                  <div className="mt-15 ph-2">
                    <label>{t("ENABLEMENT")}</label>
                  </div>

                  <div className="form-group">
                    <label
                      className="col-3 text-right"
                      htmlFor={"enablement-input"}
                    >
                      {t("ENABLE_NOTIFICATIONS")}:
                    </label>
                    <div className="col-3">
                      <ToggleSwitchInput
                        name="enablement"
                        onChange={async () => {
                          await handleToggleNotifications(
                            setFieldValue,
                            values
                          );
                        }}
                        checked={values.enablement}
                      />
                    </div>
                  </div>
                  {values.enablement && audienceId && (
                    <>
                      <hr />
                      <NotificationTypes
                        loading={enableActionFetching || enableFetching}
                        actionConfigs={actionConfigs}
                        audienceId={audienceId}
                      />
                      <hr />
                      <EmailList
                        loading={enableFetching}
                        errors={{
                          recipients: errors.recipients
                        }}
                        isValid={isValid}
                        audienceId={audienceId}
                        values={values}
                        initialValues={initialValues}
                      />
                    </>
                  )}
                </Form>
              </div>
            </Content.ScrollingList>
            <PolyPlusInactiveModal tenantId={tenant?.id!} />
          </Content>
        )}
      </Formik>
    </Loader>
  );
};

export default UserNotificationCenter;
